import { useState } from 'react';

interface CameraPermissionPromptProps {
  onPermissionGranted: () => void;
  onFallback: () => void;
}

type PromptState = 'initial' | 'requesting' | 'denied' | 'no-camera' | 'unsupported-browser';

export function CameraPermissionPrompt({
  onPermissionGranted,
  onFallback,
}: CameraPermissionPromptProps) {
  const [state, setState] = useState<PromptState>('initial');

  const handleContinue = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setState('unsupported-browser');
      return;
    }

    setState('requesting');

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      // Permission granted — stop the stream immediately since the scanner will request its own
      stream.getTracks().forEach((track) => track.stop());
      onPermissionGranted();
    } catch (error: unknown) {
      if (error instanceof DOMException) {
        if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
          setState('no-camera');
        } else {
          // NotAllowedError, SecurityError, or other permission-related errors
          setState('denied');
        }
      } else {
        setState('denied');
      }
    }
  };

  if (state === 'denied') {
    return (
      <div className="camera-permission-prompt" role="alert">
        <div className="camera-permission-icon" aria-hidden="true">
          🚫
        </div>
        <h3 className="camera-permission-title">Camera Access Required</h3>
        <p className="camera-permission-text">
          Camera access is required for QR scanning. You can enable it in your
          browser's site settings.
        </p>
        <div className="camera-permission-actions">
          <button
            className="cta-button-secondary"
            onClick={onFallback}
            type="button"
          >
            Enter Manually
          </button>
        </div>
      </div>
    );
  }

  if (state === 'no-camera') {
    return (
      <div className="camera-permission-prompt" role="alert">
        <div className="camera-permission-icon" aria-hidden="true">
          📷
        </div>
        <h3 className="camera-permission-title">No Camera Detected</h3>
        <p className="camera-permission-text">
          QR scanning isn't supported on this device. You can still add
          ingredients manually.
        </p>
        <div className="camera-permission-actions">
          <button
            className="cta-button-secondary"
            onClick={onFallback}
            type="button"
          >
            Enter Manually
          </button>
        </div>
      </div>
    );
  }

  if (state === 'unsupported-browser') {
    return (
      <div className="camera-permission-prompt" role="alert">
        <div className="camera-permission-icon" aria-hidden="true">
          🌐
        </div>
        <h3 className="camera-permission-title">Browser Not Supported</h3>
        <p className="camera-permission-text">
          QR scanning isn't supported in this browser. Try using a modern
          browser like Chrome, Firefox, or Safari, or enter your ingredient data
          manually.
        </p>
        <div className="camera-permission-actions">
          <button
            className="cta-button-secondary"
            onClick={onFallback}
            type="button"
          >
            Enter Manually
          </button>
        </div>
      </div>
    );
  }

  // Initial state: explain why camera is needed
  return (
    <div className="camera-permission-prompt">
      <div className="camera-permission-icon" aria-hidden="true">
        📸
      </div>
      <h3 className="camera-permission-title">Camera Access Needed</h3>
      <p className="camera-permission-text">
        We need access to your camera to scan the LucidGreen QR code on your
        product packaging. This lets us automatically import verified lab data
        for your ingredient.
      </p>
      <div className="camera-permission-actions">
        <button
          className="cta-button"
          onClick={handleContinue}
          disabled={state === 'requesting'}
          type="button"
        >
          {state === 'requesting' ? 'Requesting Access…' : 'Continue'}
        </button>
        <button
          className="cta-button-secondary"
          onClick={onFallback}
          type="button"
        >
          Enter Manually
        </button>
      </div>
    </div>
  );
}
